import { startCase } from 'es-toolkit';
import React from 'react';
import { connect, ResolveThunks } from 'react-redux';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

import { JobStatus, User } from '@paragon/contract-library';

import { assignUsersToJobs, setStatus, shareJobsWithOrganizations } from '../../actions/jobActions';
import { RootState } from '../../store';
import { onToggleJob } from '../../store/jobsSlice';

const statuses: readonly JobStatus[] = ['Waiting', 'OnHold', 'Complete', 'InProcess', 'Review', 'Cancelled'];

const doNotShareText = 'Do not share';

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;
type Props = StateProps & DispatchProps;

interface State {
  selectedOrganizations: string[] | null;
  selectedStatus: JobStatus | null;
  selectedAssigneeUsers: User[] | undefined;
}

class UnconnectedJobManagement extends React.Component<Props, State> {
  state: State = {
    selectedOrganizations: null, // Null for no user input, otherwise an array of organizations to share with (empty for unsharing)
    selectedStatus: null,
    selectedAssigneeUsers: [],
  };

  onShareJobs = async () => {
    if (this.state.selectedOrganizations) {
      await this.props.shareJobsWithOrganizations({
        jobIds: this.props.selectedJobIds,
        organizationNames: this.state.selectedOrganizations,
      });
    }
  };

  onAssignUser = async () => {
    await this.props.assignUsersToJobs({
      jobIds: this.props.selectedJobIds,
      userEmails: this.state.selectedAssigneeUsers!.map((user) => user.email),
    });
  };

  onSetStatus = async () => {
    if (this.state.selectedStatus != null) {
      await this.props.setStatus({
        jobIds: this.props.selectedJobIds,
        status: this.state.selectedStatus,
      });
    }
  };

  onDeselectJob(id: number) {
    this.props.onToggleJob(id);
  }

  render() {
    const jobsList = this.props.selectedJobIds.map((id) => (
      <Chip key={id} size="medium" label={id} onDelete={() => this.onDeselectJob(id)} color="secondary" />
    ));

    const isDoNotShareSelected = this.state.selectedOrganizations && this.state.selectedOrganizations.length === 0;

    return (
      <Stack sx={{ justifyContent: 'center', alignItems: 'center' }}>
        <Box sx={{ p: 2, justifyContent: 'flex-start', width: 'calc(100% * 2 / 3)' }}>
          <Card
            sx={{
              height: 100,
              bgcolor: 'backContrast.main',
              '& div': {
                m: 1,
              },
              overflow: 'scroll',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
            }}
          >
            {jobsList}
          </Card>
        </Box>
        <Grid container direction="row" sx={{ justifyContent: 'center', width: '100%' }}>
          <Grid size={{ xs: 12, lg: 3 }}>
            <Stack sx={{ p: 2, justifyContent: 'center' }}>
              <Autocomplete
                multiple
                options={this.props.users!.filter((user) => this.props.activeUser!.organization === user.organization)}
                getOptionLabel={(option) => option.email}
                renderInput={(params) => <TextField {...params} variant="outlined" label="Select Assignee(s)" />}
                value={this.state.selectedAssigneeUsers}
                onChange={(event, value) => {
                  this.setState({
                    selectedAssigneeUsers: value,
                  });
                }}
              />
              <Button sx={{ mt: 1, mb: 4, mx: 'auto', width: 300 }} variant="outlined" onClick={this.onAssignUser}>
                Assign
              </Button>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, lg: 3 }}>
            <Stack sx={{ p: 2, justifyContent: 'center' }}>
              <Autocomplete
                multiple={!isDoNotShareSelected}
                options={this.props.visibleOrganizations.concat(doNotShareText)}
                renderInput={(params) => <TextField {...params} variant="outlined" label="Select Organization(s)" />}
                value={isDoNotShareSelected ? doNotShareText : (this.state.selectedOrganizations ?? [])}
                onChange={(event, value) => {
                  if (Array.isArray(value)) {
                    // Multiselect on
                    if (value.length === 0) {
                      // The x was hit to clear all
                      this.setState({ selectedOrganizations: null });
                    } else if (value.some((organization) => organization === doNotShareText)) {
                      // "Do not share" is selected
                      this.setState({ selectedOrganizations: [] });
                    } else {
                      // An organization was selected
                      this.setState({ selectedOrganizations: value });
                    }
                    // Multiselect off
                  } else if (value == null) {
                    // The x was hit to clear all
                    this.setState({ selectedOrganizations: null });
                  } else if (value !== doNotShareText) {
                    // "Do not share" was selected but now an organization is selected
                    this.setState({ selectedOrganizations: [value] });
                  }
                }}
              />
              <Button sx={{ mt: 1, mb: 4, mx: 'auto', width: 300 }} variant="outlined" onClick={this.onShareJobs}>
                Share
              </Button>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, lg: 3 }}>
            <Stack sx={{ p: 2, justifyContent: 'center' }}>
              <FormControl variant="outlined" sx={{ minWidth: 320 }}>
                <InputLabel id="select-status-label">Select Status</InputLabel>
                <Select
                  value={this.state.selectedStatus ?? ''}
                  label="Select Status"
                  inputProps={{
                    name: 'select status',
                    id: 'select-status-label',
                  }}
                  onChange={(event) => {
                    this.setState({
                      selectedStatus: event.target.value as JobStatus,
                    });
                  }}
                >
                  {statuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      {startCase(status)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button sx={{ mt: 1, mb: 4, mx: 'auto', width: 300 }} variant="outlined" onClick={this.onSetStatus}>
                Set Status
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  activeUser: state.logInData.loggedInUser,
  selectedJobIds: state.jobs.selectedJobIds,
  users: state.organizations.usersInOrganization,
  visibleOrganizations: state.organizations.visibleOrganizations,
});

const actionCreators = {
  assignUsersToJobs,
  onToggleJob,
  setStatus,
  shareJobsWithOrganizations,
};

export default connect(mapStateToProps, actionCreators)(UnconnectedJobManagement);
